import { LanguagesEnum, languageValueToKey } from '@/api/enums';
import { phishingLanguageDisplayLabel } from '@/api/enums/phishing-languages.enum';
import { equal, handleAxiosError, ilike } from '@/api/helpers';
import { FilterCondition } from '@/api/types';
import { AppBreadCrumbTemplate } from '@/app/AppBreadCrumbTemplate';
import { Account } from '@/client/accounts/types';
import {
  CampaignMasterTemplate,
  CampaignMasterTemplatesListParams,
  phishingSignsLabels,
} from '@/client/campaigns';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import { DialogContext } from '@/common/context';
import { TranslationFunctionType } from '@/common/types';
import {
  useCampaignMasterTemplates,
  useDeleteCampaignMasterTemplate,
} from '@/hooks/query';
import { usePhishingSigns } from '@/hooks/query/phishing-signs.hooks';
import { useAppSelector } from '@/hooks/store';
import { useToast } from '@/hooks/useToast';
import { selectCurrentAccount } from '@/store/features/account/current-account.slice';
import { DialogType } from '@/system-settings/enums';
import { AppBreadCrumb } from '@/ui/breadcrumb';
import { AppButton } from '@/ui/buttons';
import AppChip from '@/ui/chip/AppChip';
import { StarRating } from '@/ui/star-rating';
import { FlexContainer } from '@/ui/styled-ui';
import { difficultyFilterOptions } from '@/utils/helpers/ui.helper';
import { AxiosError } from 'axios';
import { debounce } from 'lodash';
import { Column } from 'primereact/column';
import { DataTable, DataTableRowClickEvent } from 'primereact/datatable';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { ProgressSpinner } from 'primereact/progressspinner';
import React, {
  SyntheticEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const pathItems = (t: TranslationFunctionType, account: Account) => [
  {
    label: account?.name,
    url: RedirectPaths[RedirectPathsEnum.EDIT_ACCOUNT](account?.id),
    template: AppBreadCrumbTemplate,
  },
  {
    label: t('simulationTemplates'),
    url: RedirectPaths[RedirectPathsEnum.CAMPAIGN_MASTER_TEMPLATES](),
    template: AppBreadCrumbTemplate,
  },
];

const StyledFilterBar = styled(FlexContainer)`
  padding: var(--default-padding) 0;
  flex-wrap: wrap;
  gap: var(--small-padding);
  width: 100%;

  .p-input-icon-left {
    min-width: 200px;
    flex: 1 1 auto;
  }

  .p-dropdown {
    flex: 0 0 auto;
    width: auto;
    min-width: fit-content;
    background: transparent;
    border: 1px solid var(--purple-dark) !important;

    &:not(.p-disabled):hover {
      background: var(--red-darkest) !important;
      border-color: var(--red-darkest) !important;

      .p-dropdown-label,
      .p-dropdown-trigger-icon {
        color: var(--white-main);
      }
    }

    .p-dropdown-label {
      background: transparent;
      padding-right: 0;
    }

    &.p-dropdown-clearable .p-dropdown-label {
      padding-right: 1.5rem !important;
    }

    .p-dropdown-trigger {
      background: transparent;
    }
  }

  .p-inputtext {
    border: 2px solid transparent;

    &:enabled:hover {
      border: 2px solid var(--red-darkest);
    }

    &:enabled:focus {
      border: 2px solid var(--red-darkest);
      box-shadow: none;
    }
  }
`;

const LanguageBubblesContainer = styled(FlexContainer)`
  flex-wrap: wrap;
  gap: 5px;
  justify-content: flex-start;
`;

const LanguageBubble = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  height: 20px;
  padding: 0 var(--xsmall-padding);
  border-radius: var(--xsmall-border-radius);
  background-color: #eaeae8;
  font-size: var(--xsmall-font-size);
  font-weight: 500;
  color: var(--black-main);
`;

const OverflowCount = styled.span`
  font-size: var(--xsmall-font-size);
  font-weight: 500;
  color: var(--black-main);
  line-height: 20px;
  cursor: pointer;
`;

const CollapseButton = styled.span`
  font-size: var(--xsmall-font-size);
  font-weight: 500;
  color: var(--black-main);
  line-height: 20px;
  cursor: pointer;
`;

const StatusPill = styled.span<{ $live: boolean }>`
  display: inline-block;
  padding: var(--xxxsmall-padding) var(--small-padding);
  border-radius: 11px;
  font-size: var(--xsmall-font-size);
  font-weight: 500;
  background-color: ${({ $live }) => ($live ? '#e6f5f0' : '#faf4de')};
  color: ${({ $live }) => ($live ? '#498778' : '#a16207')};
`;

const NameText = styled.div`
  font-size: var(--small-font-size);
  font-weight: 500;
  color: var(--black-main);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DescriptionText = styled.div`
  font-size: var(--xxsmall-font-size);
  font-weight: 500;
  color: #8e8e8e;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: var(--xxxsmall-padding);
`;

const TableWrapper = styled.div`
  border: 1px solid var(--gray-dark);
  border-radius: var(--medium-border-radius);
  overflow: hidden;

  .p-datatable-thead > tr > th {
    background-color: var(--beige-main);
    font-size: var(--xsmall-font-size);
    font-weight: 700;
    border: none;
    padding: var(--medium-padding);

    &.p-sortable-column:focus {
      box-shadow: var(--box-shadow-red-inset);
    }

    &.p-highlight,
    &.p-highlight:not(.p-sortable-disabled):hover {
      background-color: var(--beige-main);
      color: var(--red-main);
    }

    .p-sortable-column-icon {
      display: none;
    }
  }

  .p-datatable-tbody > tr {
    border: none;
    cursor: pointer;
    transition: background-color 0.2s;

    > td {
      border: none;
      padding: var(--default-padding) var(--medium-padding);
    }

    &:nth-child(even) {
      background-color: var(--beige-main);
    }

    &:nth-child(odd) {
      background-color: white;
    }

    &:not(.p-datatable-row-expansion):hover {
      background-color: var(--orange-pale) !important;
      color: var(--black-main) !important;
    }
  }

  .p-paginator {
    border: none;
  }
`;

const MAX_VISIBLE_LANGUAGES = 5;

const ADMIN_LANGUAGE_OPTIONS = Object.values(LanguagesEnum).map((lang) => ({
  label: phishingLanguageDisplayLabel[lang] ?? lang,
  value: lang,
}));

const STATUS_OPTIONS = [
  { label: 'Live', value: 'true' },
  { label: 'Draft', value: 'false' },
];

export const CampaignMasterTemplatesPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentAccount = useAppSelector(selectCurrentAccount);
  const { remove } = useDeleteCampaignMasterTemplate();
  const toast = useToast();
  const { setDialogData } = useContext(DialogContext);
  const { phishingSigns } = usePhishingSigns();

  const TAKE = 20;
  const [page, setPage] = useState(1);
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('');
  const [phishingSignId, setPhishingSignId] = useState<string | undefined>(
    undefined,
  );
  const [language, setLanguage] = useState<string | undefined>(undefined);
  const [difficulty, setDifficulty] = useState<number | undefined>(undefined);
  const [active, setActive] = useState<boolean | undefined>(undefined);
  const [sortBy, setSortBy] = useState<string | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [expandedLanguages, setExpandedLanguages] = useState<Set<string>>(
    new Set(),
  );

  const params = useMemo<CampaignMasterTemplatesListParams>(() => {
    const filters: FilterCondition[] = [];
    if (debouncedSearchValue) {
      filters.push(ilike('translations.name', debouncedSearchValue));
    }
    if (phishingSignId) filters.push(equal('phishingSign', phishingSignId));
    if (difficulty !== undefined) filters.push(equal('difficulty', difficulty));
    if (active !== undefined) filters.push(equal('active', active));
    if (language) filters.push(equal('language', language));

    return {
      take: TAKE,
      skip: (page - 1) * TAKE,
      filters,
      sort: sortBy ? [`${sortBy},${sortOrder}`] : undefined,
    };
  }, [
    page,
    debouncedSearchValue,
    phishingSignId,
    language,
    difficulty,
    active,
    sortBy,
    sortOrder,
  ]);

  const { masterTemplates, isLoading } = useCampaignMasterTemplates(params);

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        setPage(1);
        setDebouncedSearchValue(value);
      }, 300),
    [],
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const labels = useMemo(() => phishingSignsLabels(t), [t]);

  const phishingSignFilterOptions = useMemo(
    () =>
      (phishingSigns?.result ?? []).map((sign) => ({
        label: labels[sign.key as keyof typeof labels] ?? sign.key,
        value: sign.id,
      })),
    [phishingSigns, labels],
  );

  const onPage = useCallback((next: number) => {
    setPage(next);
  }, []);

  const onSort = useCallback((field: string, order: 'asc' | 'desc') => {
    setSortBy(field);
    setSortOrder(order);
  }, []);

  const handleDelete = useCallback(
    (template: CampaignMasterTemplate) => {
      const name =
        template.translations.find(
          (tr) => tr.language === LanguagesEnum.EN && tr.name,
        )?.name ??
        template.translations.find((tr) => tr.name)?.name ??
        template.id;

      setDialogData({
        type: DialogType.CONFIRMATION,
        show: true,
        header: t('simulationTemplates.deleteConfirm.header'),
        message: t('simulationTemplates.deleteConfirm.message', { name }),
        onAccept: async () => {
          try {
            await remove(template.id);
            toast?.success(t('toast.success'), t('deleteSuccess'));
          } catch (e) {
            handleAxiosError(e as Error | AxiosError, toast);
          }
        },
      });
    },
    [remove, t, toast, setDialogData],
  );

  const nameBodyTemplate = (row: CampaignMasterTemplate) => {
    const translation =
      row.translations.find(
        (tr) => tr.language === LanguagesEnum.EN && tr.name,
      ) ?? row.translations.find((tr) => tr.name);
    return (
      <div>
        <NameText>{translation?.name ?? '—'}</NameText>
        {translation?.description && (
          <DescriptionText
            dangerouslySetInnerHTML={{
              __html:
                translation.description.length > 100
                  ? `${translation.description.substring(0, 100)}...`
                  : translation.description,
            }}
          />
        )}
      </div>
    );
  };

  const phishingSignBodyTemplate = (row: CampaignMasterTemplate) => {
    if (!row.phishingSign) return '—';
    const label =
      labels[row.phishingSign.key as keyof typeof labels] ??
      row.phishingSign.key;
    return (
      <AppChip
        label={label}
        type={`phishing-${row.phishingSign.key}` as any}
        style={{ fontSize: 'var(--xsmall-font-size)', height: '20px' }}
      />
    );
  };

  const difficultyBodyTemplate = (row: CampaignMasterTemplate) => {
    if (row.difficulty == null) return '—';
    return (
      <StarRating value={row.difficulty} maxStars={5} icon="fish" disabled />
    );
  };

  const languagesBodyTemplate = (row: CampaignMasterTemplate) => {
    const languages = (row.simulationLanguages ?? []).map(
      (lang) => languageValueToKey[lang] ?? lang,
    );
    const isExpanded = expandedLanguages.has(row.id);
    const displayed = isExpanded
      ? languages
      : languages.slice(0, MAX_VISIBLE_LANGUAGES);
    const overflow = languages.length - MAX_VISIBLE_LANGUAGES;

    const toggleExpand = (e: React.MouseEvent) => {
      e.stopPropagation();
      setExpandedLanguages((prev) => {
        const next = new Set(prev);
        if (next.has(row.id)) next.delete(row.id);
        else next.add(row.id);
        return next;
      });
    };

    return (
      <LanguageBubblesContainer>
        {displayed.map((lang) => (
          <LanguageBubble key={lang}>{lang}</LanguageBubble>
        ))}
        {!isExpanded && overflow > 0 && (
          <OverflowCount onClick={toggleExpand}>+{overflow}</OverflowCount>
        )}
        {isExpanded && overflow > 0 && (
          <CollapseButton onClick={toggleExpand}>
            <i className="pi pi-chevron-up" style={{ fontSize: '10px' }} />
          </CollapseButton>
        )}
      </LanguageBubblesContainer>
    );
  };

  const statusBodyTemplate = (row: CampaignMasterTemplate) => (
    <StatusPill $live={row.active}>
      {row.active
        ? t('simulationTemplates.status.live')
        : t('simulationTemplates.status.draft')}
    </StatusPill>
  );

  const actionsBodyTemplate = (row: CampaignMasterTemplate) => (
    <FlexContainer gap="var(--xsmall-padding)" justify="flex-end">
      <AppButton
        icon="pi pi-trash"
        type="text"
        severity="danger"
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          handleDelete(row);
        }}
      />
    </FlexContainer>
  );

  return (
    <>
      <AppBreadCrumb model={pathItems(t, currentAccount as Account)} />

      <FlexContainer justify="space-between" align="center" className="mb-4">
        <h1>{t('simulationTemplates')}</h1>
        <AppButton
          label={t('simulationTemplates.create')}
          icon="pi pi-plus"
          onClick={() =>
            navigate(
              RedirectPaths[
                RedirectPathsEnum.CAMPAIGN_MASTER_TEMPLATES_CREATE
              ](),
            )
          }
        />
      </FlexContainer>

      <StyledFilterBar>
        <div className="p-input-icon-left">
          <i className="pi pi-search" />
          <InputText
            className="w-full"
            value={searchValue}
            onInput={(event: SyntheticEvent<HTMLInputElement>) => {
              const value = (event.target as HTMLInputElement).value;
              setSearchValue(value);
              debouncedSearch(value);
            }}
            placeholder={t('simulationTemplates.searchPlaceholder')}
            autoComplete="off"
          />
        </div>

        <Dropdown
          value={phishingSignId ?? null}
          options={phishingSignFilterOptions}
          onChange={(e) => {
            setPage(1);
            setPhishingSignId(e.value ?? undefined);
          }}
          placeholder={t('simulationTemplates.filter.phishingSigns')}
          showClear={!!phishingSignId}
        />

        <Dropdown
          value={language ?? null}
          options={ADMIN_LANGUAGE_OPTIONS}
          onChange={(e) => {
            setPage(1);
            setLanguage(e.value ?? undefined);
          }}
          placeholder={t('simulationTemplates.filter.languages')}
          showClear={!!language}
        />

        <Dropdown
          value={difficulty ?? null}
          options={difficultyFilterOptions}
          onChange={(e) => {
            setPage(1);
            setDifficulty(e.value ?? undefined);
          }}
          placeholder={t('simulationTemplates.filter.difficulty')}
          showClear={difficulty !== undefined}
        />

        <Dropdown
          value={active !== undefined ? String(active) : null}
          options={STATUS_OPTIONS}
          onChange={(e) => {
            setPage(1);
            setActive(e.value ? e.value === 'true' : undefined);
          }}
          placeholder={t('simulationTemplates.filter.status')}
          showClear={active !== undefined}
        />
      </StyledFilterBar>

      {isLoading ? (
        <FlexContainer justify="center" className="p-4">
          <ProgressSpinner />
        </FlexContainer>
      ) : (
        <TableWrapper>
          <DataTable
            value={masterTemplates?.result ?? []}
            paginator
            rows={TAKE}
            totalRecords={masterTemplates?.count ?? 0}
            lazy
            cellMemo={false}
            first={(page - 1) * TAKE}
            onPage={(e) => onPage(Math.floor(e.first / e.rows) + 1)}
            onSort={(e) =>
              onSort(e.sortField as string, e.sortOrder === 1 ? 'asc' : 'desc')
            }
            sortField={sortBy}
            sortOrder={sortOrder === 'desc' ? -1 : 1}
            emptyMessage={t('noResults')}
            dataKey="id"
            onRowClick={(e: DataTableRowClickEvent) =>
              navigate(
                RedirectPaths[RedirectPathsEnum.CAMPAIGN_MASTER_TEMPLATES_EDIT](
                  (e.data as CampaignMasterTemplate).id,
                ),
              )
            }
          >
            <Column
              field="name"
              header={t('name')}
              body={nameBodyTemplate}
              sortable
            />
            <Column
              field="phishingSign"
              header={t('phishingSign')}
              body={phishingSignBodyTemplate}
              sortable
            />
            <Column
              field="difficulty"
              header={t('difficulty')}
              body={difficultyBodyTemplate}
              sortable
            />
            <Column header={t('languages')} body={languagesBodyTemplate} />
            <Column
              field="active"
              header={t('status')}
              body={statusBodyTemplate}
              sortable
            />
            <Column body={actionsBodyTemplate} style={{ width: '100px' }} />
          </DataTable>
        </TableWrapper>
      )}
    </>
  );
};
